import { PDFDocument } from 'pdf-lib';
import { directSupabase } from '../../lib/supabaseClient';
import { pdfjsWorkerSrc } from '../../utils/pdfjsWorkerSrc';
import type { OrderAcceptanceCopy } from './jobCard.types';
import {
  isPdfOaFile,
  isRedactableOaFile,
  moneyBoxesFromTextRects,
  paintPriceBoxes,
  parsePriceBoxes,
  redactedFileName,
  type PdfTextRect,
  type PriceBox,
} from './oaRedact';

const OA_REDACTED_BUCKET = 'order-acceptance-redacted';

export type OaRedactProgress = (message: string) => void;

function storageSafeName(name: string) {
  return name.replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 180) || 'file';
}

function table(name: string) {
  return (directSupabase as any).from(name);
}

function invokeErrorMessage(error: any, data: any): string {
  if (data?.error === 'invalid_api_key') return 'Invalid OpenAI API key. Please update the key and try again.';
  if (data?.error === 'missing_api_key') return 'AI service is not configured. Please contact support.';
  if (data?.error === 'insufficient_quota') return 'AI service quota exceeded. Please try again later.';
  if (data?.error === 'upstream_error') return 'AI service is temporarily unavailable. Please try again later.';
  if (data?.detail) return String(data.detail);
  if (error?.message) return String(error.message);
  return 'Could not find prices on this file.';
}

async function locatePriceBoxes(imageDataUrl: string): Promise<PriceBox[]> {
  const openaiKey = (typeof process !== 'undefined' && process.env && (process.env.OPENAI_API_KEY || process.env.REACT_APP_OPENAI_API_KEY)) || undefined;
  let authHeader = '';
  try {
    const { data: { session } } = await (directSupabase as any).auth.getSession();
    if (session?.access_token) authHeader = `Bearer ${session.access_token}`;
  } catch {
    // Session is optional; the edge function still accepts the gateway key.
  }

  const payload = {
    imageDataUrl,
    ...(openaiKey ? { apiKey: openaiKey } : {}),
  };

  const resp = await (directSupabase as any).functions.invoke('ai-oa-redact-locate', {
    body: payload,
    ...(authHeader ? { headers: { Authorization: authHeader } } : {}),
  });
  let { data, error } = resp as any;

  if (error && String(error.message || '').includes('Failed to send a request')) {
    const baseUrl = (directSupabase as any).functions?._url || `${(directSupabase as any).supabaseUrl}/functions/v1`;
    const url = `${baseUrl}/ai-oa-redact-locate`;
    const res = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      error = { message: `HTTP ${res.status}` };
      data = await res.json().catch(async () => ({ detail: await res.text() }));
    } else {
      data = await res.json();
      error = null;
    }
  }

  if (error || data?.ok === false) {
    throw new Error(invokeErrorMessage(error, data));
  }
  return parsePriceBoxes(data?.data || data);
}

function canvasToLocateDataUrl(source: HTMLCanvasElement): string {
  const max = 1280;
  const scale = Math.min(1, max / Math.max(source.width, source.height));
  if (scale >= 1) return source.toDataURL('image/jpeg', 0.72);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(source.width * scale));
  canvas.height = Math.max(1, Math.round(source.height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not prepare this page for redaction.');
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL('image/jpeg', 0.72);
}

async function redactCanvas(canvas: HTMLCanvasElement, knownBoxes?: PriceBox[]): Promise<number> {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not open this page for redaction.');
  const boxes = knownBoxes && knownBoxes.length
    ? knownBoxes
    : await locatePriceBoxes(canvasToLocateDataUrl(canvas));
  paintPriceBoxes(ctx, canvas.width, canvas.height, boxes);
  return boxes.length;
}

type PdfJsTextItem = { str: string; width: number; height: number; transform: number[] };

function isPdfTextItem(item: unknown): item is PdfJsTextItem {
  if (!item || typeof item !== 'object') return false;
  const row = item as { str?: unknown; transform?: unknown };
  return typeof row.str === 'string' && Array.isArray(row.transform) && row.transform.length >= 6;
}

function pdfTextRectsFromItems(
  items: readonly unknown[],
  viewport: { transform: number[]; scale: number },
  transform: (a: number[], b: number[]) => number[],
): PdfTextRect[] {
  const rects: PdfTextRect[] = [];
  items.forEach((item) => {
    if (!isPdfTextItem(item)) return;
    rects.push(pdfTextRect(item, viewport, transform));
  });
  return rects;
}

function pdfTextRect(
  item: PdfJsTextItem,
  viewport: { transform: number[]; scale: number },
  transform: (a: number[], b: number[]) => number[],
): PdfTextRect {
  const tx = transform(viewport.transform, item.transform);
  const fontHeight = Math.hypot(tx[2], tx[3]) || (viewport.scale * 10);
  const width = Math.max(1, (item.width || 0) * (viewport.scale || 1));
  const height = Math.max(
    fontHeight * 0.85,
    (item.height || 0) * (viewport.scale || 1),
    8,
  );
  return {
    str: item.str,
    x: tx[4],
    y: tx[5] - height,
    w: width,
    h: height,
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) reject(new Error('Could not save the redacted page.'));
      else resolve(blob);
    }, type, quality);
  });
}

async function loadImageCanvas(blob: Blob): Promise<HTMLCanvasElement> {
  const url = URL.createObjectURL(blob);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Could not open this image for redaction.'));
      el.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, image.naturalWidth || image.width);
    canvas.height = Math.max(1, image.naturalHeight || image.height);
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not open this image for redaction.');
    ctx.drawImage(image, 0, 0);
    return canvas;
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function redactImage(blob: Blob, name: string, contentType?: string | null): Promise<{ blob: Blob; contentType: string; found: number }> {
  const canvas = await loadImageCanvas(blob);
  const found = await redactCanvas(canvas);
  const type = String(contentType || blob.type || '').toLowerCase().includes('png') || name.toLowerCase().endsWith('.png')
    ? 'image/png'
    : 'image/jpeg';
  return { blob: await canvasToBlob(canvas, type, type === 'image/jpeg' ? 0.9 : undefined), contentType: type, found };
}

async function renderPdfPages(blob: Blob): Promise<Array<{ canvas: HTMLCanvasElement; textBoxes: PriceBox[] }>> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorkerSrc;
  const pdf = await pdfjs.getDocument({ data: await blob.arrayBuffer() }).promise;
  const pages: Array<{ canvas: HTMLCanvasElement; textBoxes: PriceBox[] }> = [];
  const scale = Math.min(2.5, Math.max(2, (typeof window !== 'undefined' ? window.devicePixelRatio : 2) || 2));
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum += 1) {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale });
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not open this PDF for redaction.');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvas, canvasContext: ctx, viewport }).promise;
    const text = await page.getTextContent();
    const rects = pdfTextRectsFromItems(text.items, viewport, pdfjs.Util.transform);
    pages.push({
      canvas,
      textBoxes: moneyBoxesFromTextRects(rects, canvas.width, canvas.height),
    });
  }
  return pages;
}

async function redactPdf(blob: Blob, onProgress?: OaRedactProgress): Promise<{ blob: Blob; contentType: string; found: number }> {
  const pages = await renderPdfPages(blob);
  const pdf = await PDFDocument.create();
  let found = 0;
  for (let index = 0; index < pages.length; index += 1) {
    onProgress?.(`Page ${index + 1} of ${pages.length}`);
    found += await redactCanvas(pages[index].canvas, pages[index].textBoxes);
    const jpeg = await canvasToBlob(pages[index].canvas, 'image/jpeg', 0.92);
    const image = await pdf.embedJpg(await jpeg.arrayBuffer());
    const page = pdf.addPage([image.width, image.height]);
    page.drawImage(image, { x: 0, y: 0, width: image.width, height: image.height });
  }
  return {
    blob: new Blob([await pdf.save()], { type: 'application/pdf' }),
    contentType: 'application/pdf',
    found,
  };
}

export async function saveOrderAcceptanceRedaction(input: {
  id: string;
  projectId: string;
  driveItemId: string;
  originalName: string;
  blob: Blob;
  contentType: string;
}): Promise<string> {
  const name = redactedFileName(input.originalName);
  const storagePath = `${input.projectId}/${input.driveItemId}/${storageSafeName(name)}`;
  const uploaded = await directSupabase.storage
    .from(OA_REDACTED_BUCKET)
    .upload(storagePath, input.blob, { contentType: input.contentType, upsert: true });
  if (uploaded.error) {
    if (/bucket|not found|does not exist/i.test(uploaded.error.message)) {
      throw new Error('Apply the OA price-redact migration, then try again.');
    }
    throw new Error(uploaded.error.message);
  }
  const { error } = await table('project_order_acceptance_files')
    .update({
      redacted_name: name,
      redacted_storage_path: storagePath,
      redacted_content_type: input.contentType,
      redacted_at: new Date().toISOString(),
    })
    .eq('id', input.id);
  if (error) {
    if (/redacted_name|redacted_storage_path|redacted_content_type|redacted_at/i.test(error.message)) {
      throw new Error('Apply the OA price-redact migration, then try again.');
    }
    throw new Error(error.message);
  }
  return name;
}

export async function redactOrderAcceptanceCopy(
  file: OrderAcceptanceCopy,
  original: Blob,
  onProgress?: OaRedactProgress,
): Promise<{ name: string; found: number }> {
  if (!isRedactableOaFile(file.name, file.content_type || original.type)) {
    throw new Error(`${file.name} is not a PDF or a common image, so it cannot be redacted.`);
  }
  const result = isPdfOaFile(file.name, file.content_type || original.type)
    ? await redactPdf(original, onProgress)
    : await redactImage(original, file.name, file.content_type || original.type);
  const name = await saveOrderAcceptanceRedaction({
    id: file.id,
    projectId: file.project_id,
    driveItemId: file.drive_item_id,
    originalName: file.name,
    blob: result.blob,
    contentType: result.contentType,
  });
  return { name, found: result.found };
}
